// React
import React, { useEffect, useState } from "react";
import { useObserver } from "mobx-react-lite";

// Core
import { useRouter } from "Core/Base";
import { Loader } from "Core/Components/Loader";

// Custom
import { Error } from "Custom/Components/Error";
import { FooterMenuBlock } from "./FooterMenuBlock";
import { DashboardPageStyles, MainPageStyles } from "Custom/StylesAppSpecific/DashboardPagesStyling";

// Models
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";

// Material UI
import { Box } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

// SVG Icons
import { ReactComponent as DashboardLogo } from "../Content/dashboard.svg";
import { ReactComponent as Home } from "../Content/home.svg";
import { ReactComponent as Document } from "../Content/document.svg";
import { ReactComponent as Touch } from "../Content/touch.svg";
import { ReactComponent as Cardpayment } from "../Content/cardpayment.svg";
import { ReactComponent as Tool } from "../Content/tool.svg";
import { DashboardViewModel } from "Custom/ViewModels/DashboardViewModel";

export const PageMenu: React.FC = () => {
    // #region Initialization
    const router = useRouter();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md")) ? true : false;

    const mainPageStyles = MainPageStyles();
    const dashboardPageStyles = DashboardPageStyles();

    // Use the shared viewModel
    const [viewModel, setViewModel] = useState(() => new DashboardViewModel((router.match.params as any).propertyId));

    useEffect(() => {
        const newPropertyId = (router.match.params as any).propertyId;
        if (newPropertyId !== viewModel.propertyId) {
            viewModel.changePlot(newPropertyId);
            setViewModel(new DashboardViewModel(newPropertyId));
        }
    }, [(router.match.params as any).propertyId]);
    // #endregion Initialization

    // #region Render Handlers
    const renderBusy = () => <Loader />;
    const renderError = () => <Error errorText="Failed to retrieve information." />;

    const renderSidebarMenu = () => {
        if (!isDesktop) return null;
        const menuItems = [
            { title: "Dashboard", path: viewModel.navigateToDashboard, icon: DashboardLogo, ordinal: 0 },
            { title: "My Home", path: viewModel.navigateToPropertyPathname, icon: Home, ordinal: 1 },
            { title: "Documents", path: viewModel.navigateToDocumentsPathname, icon: Document, ordinal: 4 },
            { title: "Payments", path: viewModel.navigateToPaymentPathname, icon: Cardpayment, ordinal: 5 },
        ];

        if (viewModel.canViewIsChoicesToPlot) {
            menuItems.push({ title: "Choices", path: viewModel.navigateToChoicesPathname, icon: Touch, ordinal: 2 });
        }

        if (viewModel.canViewSnags && viewModel.canViewReportSnags) {
            menuItems.push({ title: "Snags", path: viewModel.navigateToSnagsPathname, icon: Tool, ordinal: 3 });
        }

        return (
            <Box className={`${dashboardPageStyles.sidebar} baseSidebar`}>
                <Box className={mainPageStyles.sidebarMenu}>
                    {menuItems
                        .sort((a, b) => a.ordinal - b.ordinal)
                        .map((item, index) => (
                            <Box key={index} className={mainPageStyles.sidebarMenuItem}>
                                <FooterMenuBlock title={item.title} icon={item.icon} path={item.path || ""} height="40px" width="40px" />
                            </Box>
                        ))}
                </Box>
            </Box>
        );
    };

    const renderFooterMenu = () => {
        if (isDesktop) return null;

        return (
            <Box className={mainPageStyles.stickyFooterMenu}>
                <FooterMenuBlock title={"Dashboard"} path={viewModel.navigateToDashboard} icon={DashboardLogo} height="24px" width="25px" />
                <FooterMenuBlock title={"My home"} path={viewModel.navigateToPropertyPathname} icon={Home} height="24px" width="25px" />
                {viewModel.canViewIsChoicesToPlot && <FooterMenuBlock title={"Choices"} path={viewModel.navigateToChoicesPathname} icon={Touch} height="24px" width="25px" />}
                {viewModel.canViewSnags && viewModel.canViewReportSnags && (
                    <FooterMenuBlock title={"Snags"} path={viewModel.navigateToSnagsPathname} icon={Tool} height="24px" width="25px" />
                )}
                <FooterMenuBlock title={"Documents"} path={viewModel.navigateToDocumentsPathname} icon={Document} height="24px" width="25px" />
                <FooterMenuBlock title={"Payments"} path={viewModel.navigateToPaymentPathname} icon={Cardpayment} height="24px" width="25px" />
            </Box>
        );
    };

    const renderItems = () => (
        <Box>
            {renderSidebarMenu()}
            {renderFooterMenu()}
        </Box>
    );
    // #endregion Render Handlers

    // #region Main Component
    return useObserver(() => (
        <Box>
            <Box className={mainPageStyles.workspace}>
                {renderItems()}
                {viewModel.ApiStatus === ApiStatus.error && renderError()}
            </Box>
        </Box>
    ));
    // #endregion Main Component
};
